import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, ArrowRight, Smartphone } from 'lucide-react';
import { CREW_WELLNESS_URL, getConfiguredCrewEntry } from '../components/crewPortal.js';
import CrewIntakeGuide from '../components/CrewIntakeGuide.jsx';

const SUPPORT_EMAIL = 'hello@seascope.tech';
const GATE_STORAGE_KEY = 'seascope_crew_portal_gate';

/**
 * This is a DOORWAY to information pages on the marketing site, not a
 * credential — nothing medical lives behind it, and the clinical platform
 * checks the real crew-portal password server-side (`crew_portal_password()`
 * / `open_gate` in backend/app/peme/crew_portal.py). We only compare a
 * SHA-256 hex digest client-side so the plaintext never ships in the bundle.
 *
 * Rotate by recomputing: printf %s '<new password>' | sha256sum
 */
const GATE_PASSWORD_SHA256 = '73c1dda1396dfbcc6c9061905473c3c19bb2dca58d2e64d46a7737841d7ba36c';

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function GateScreen({ onUnlock }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setChecking(true);
    const hex = await sha256Hex(password);
    setChecking(false);
    if (hex === GATE_PASSWORD_SHA256) {
      sessionStorage.setItem(GATE_STORAGE_KEY, '1');
      setError('');
      onUnlock();
    } else {
      // ONE plain message. No hints, no attempt counter.
      setError('That password is not correct.');
      setPassword('');
    }
  };

  return (
    <header className="bg-white py-24 lg:py-32">
      <div className="container-wide">
        <div className="mx-auto max-w-md rounded-card border border-line bg-white p-8 shadow-soft lg:p-12">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-teal-tint">
              <Lock className="h-6 w-6 text-teal-strong" aria-hidden="true" />
            </div>
            <span className="label-caps mb-3 block">Crew portal</span>
            <h1 className="mb-3 text-[28px] font-medium leading-[34px] lg:text-[32px]">
              Enter the crew portal password
            </h1>
            <p className="text-[15px] leading-7 text-ink-2">
              This password opens information about the SeaScope crew portal and the separate
              Crew Wellness app. Your clinical application still checks its own entry password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="crew-portal-password" className="mb-2 block text-[14px] font-medium text-ink">
                Password
              </label>
              <input
                type="password"
                id="crew-portal-password"
                name="password"
                autoComplete="off"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-btn border border-line px-4 py-3 text-[16px] text-ink focus:border-teal-strong focus:outline-none focus:ring-2 focus:ring-teal/40"
                required
              />
              {error && (
                <p role="alert" className="mt-2 text-[14px] text-red-600">
                  {error}
                </p>
              )}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={checking}>
              {checking ? 'Checking…' : 'Continue'}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>

          <p className="mt-6 text-center text-[14px] leading-6">
            <Link to="/peme#crew" className="text-link">Read the crew intake guide</Link>
          </p>
          <p className="mt-6 text-center text-[14px] leading-6 text-ink-3">
            Don&rsquo;t have a password? Ask your crewing agency or partner, or write to{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-link">
              {SUPPORT_EMAIL}
            </a>{' '}
            to request access.
          </p>
        </div>
      </div>
    </header>
  );
}

function InfoHub({ entry }) {
  return (
    <>
      <header className="bg-white py-24 lg:py-32">
        <div className="container-wide max-w-3xl">
          <span className="label-caps mb-6 block">Crew portal</span>
          <h1 className="mb-8 text-[36px] font-medium leading-[42px] lg:text-[56px] lg:leading-[64px]">
            What the crew portal is, and how to get in
          </h1>
          <p className="text-[17px] leading-7 text-ink-2 lg:text-[18px] lg:leading-[30px]">
            The medical portal is for Form A before an examination. The separate Crew Wellness app
            is for readings between medicals. Choose the route your agency or clinic gave you.
          </p>
        </div>
      </header>

      <section className="border-t border-line bg-bg-alt py-16 lg:py-24">
        <div className="container-wide">
          <CrewIntakeGuide entry={entry} />
        </div>
      </section>

      <section className="border-t border-line bg-white py-16 lg:py-24">
        <div className="container-wide max-w-3xl">
          <div className="rounded-card border border-line bg-white p-8">
            <Smartphone className="mb-5 h-6 w-6 text-teal" aria-hidden="true" />
            <h2 className="mb-3 text-xl font-medium text-ink">Crew Wellness app is separate</h2>
            <p className="mb-4 leading-7 text-ink-2">
              Use this app for readings between medicals. It does not collect your PEME Form A or
              issue an examination certificate.
            </p>
            <a href={CREW_WELLNESS_URL} target="_blank" rel="noopener noreferrer" className="text-link font-medium">
              Open the Crew Wellness app
            </a>
          </div>
        </div>
      </section>
    </>
  );
}

export default function CrewPortal() {
  const [unlocked, setUnlocked] = useState(false);
  const entry = getConfiguredCrewEntry();

  useEffect(() => {
    if (sessionStorage.getItem(GATE_STORAGE_KEY) === '1') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUnlocked(true);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Crew portal | SeaScope</title>
        <meta
          name="description"
          content="For seafarers: what the SeaScope crew portal is, how to fill in Form A, and how to get into the Crew Wellness app."
        />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://seascope.tech/crew-portal" />
      </Helmet>

      {unlocked ? <InfoHub entry={entry} /> : <GateScreen onUnlock={() => setUnlocked(true)} />}
    </>
  );
}
